// Task pieces rendered on calendars: draggable chips / bars, timed blocks, side-list rows,
// the "+N more" day popover and the inline quick-add input.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useDraggable } from '@dnd-kit/core';
import type { Task } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses, statusOf } from '@/lib/hierarchy';
import { useOpenTask } from '@/lib/nav';
import { dueTone, formatDue } from '@/lib/format';
import { rgba } from '@/lib/color';
import { openContextMenu } from '@/components/ui/overlays';
import { Check, CornerDownRight } from '@/components/ui/icons';
import { StatusIcon, statusProgress } from '@/components/fields/Status';
import { TaskContextMenu } from '@/task/TaskContextMenu';
import { useCalDnd, type CalDragData } from './CalendarDnd';
import { formatClock, minutesOfDay, type Seg } from './calendarUtils';

function useTaskStatus(task: Task) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  return { status, progress: statusProgress(statuses, status), closed: isCompleteType(status.type) };
}

function useTaskHandlers(task: Task) {
  const openTask = useOpenTask();
  const { suppressClick } = useCalDnd();
  return {
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (suppressClick()) return;
      openTask(task.id);
    },
    onContextMenu: (e: React.MouseEvent) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />),
  };
}

// ---------------------------------------------------------------------------

/** Chip / continuous bar for month rows and the all-day lane, filled with a tint of the status color
 *  and edged with the color itself. */
export function TaskChip({ seg, style, idPrefix, showTime = true }: { seg: Seg; style?: CSSProperties; idPrefix: string; showTime?: boolean }) {
  const { task } = seg;
  const { status, closed } = useTaskStatus(task);
  const handlers = useTaskHandlers(task);
  const segDays = seg.endCol - seg.startCol + 1;
  const data: CalDragData = { taskId: task.id, segStart: seg.segStart, segDays };
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: `${idPrefix}:${task.id}:${seg.segStart}`, data });
  const time = showTime && task.dueTime && task.dueDate !== null && !seg.contRight && segDays === 1 ? formatClock(minutesOfDay(task.dueDate)) : null;
  const multi = segDays > 1 || seg.contLeft || seg.contRight;
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={clsx('cal-chip', { multi, closed, dragging: isDragging, 'cont-left': seg.contLeft, 'cont-right': seg.contRight })}
      style={{ ...style, '--chip-accent': status.color } as CSSProperties}
      title={`${task.name} · ${status.name}`}
      {...handlers}
    >
      {closed && <Check size={12} strokeWidth={3} className="cal-chip-done" />}
      {task.parentId && <CornerDownRight size={12} className="cal-chip-sub" />}
      {time && <span className="cal-chip-time">{time}</span>}
      <span className="cal-chip-name truncate">{task.name}</span>
    </div>
  );
}

/** Task placed at its hour in the week / day grid. */
export function TimedBlock({ task, style, compact }: { task: Task; style?: CSSProperties; compact?: boolean }) {
  const { status, progress, closed } = useTaskStatus(task);
  const handlers = useTaskHandlers(task);
  const data: CalDragData = { taskId: task.id, timed: true };
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: `timed:${task.id}`, data });
  const time = task.dueDate !== null ? formatClock(minutesOfDay(task.dueDate)) : '';
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={clsx('cal-timed', { closed, dragging: isDragging, compact })}
      style={{ ...style, '--chip-accent': status.color, '--chip-tint': rgba(status.color, 0.16) } as CSSProperties}
      title={`${time} · ${task.name}`}
      {...handlers}
    >
      <div className="cal-timed-line">
        <StatusIcon type={status.type} color={status.color} size={12} progress={progress} />
        <span className="cal-timed-name truncate">{task.name}</span>
        {compact && <span className="cal-chip-time">{time}</span>}
      </div>
      {!compact && <div className="cal-timed-time">{time}</div>}
    </div>
  );
}

/** Row in the Unscheduled / Overdue panel and the Planner task list. */
export function TaskListRow({ task, idPrefix, showDue = false, className }: { task: Task; idPrefix: string; showDue?: boolean; className?: string }) {
  const { status, progress, closed } = useTaskStatus(task);
  const handlers = useTaskHandlers(task);
  const listName = useDb((db) => db.lists[task.listId]?.name ?? '');
  const data: CalDragData = { taskId: task.id };
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: `${idPrefix}:${task.id}`, data });
  const tone = dueTone(task.dueDate, closed);
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={clsx('cal-row', className, { closed, dragging: isDragging })} {...handlers}>
      <StatusIcon type={status.type} color={status.color} size={14} progress={progress} />
      <div className="cal-row-main">
        <div className="cal-row-name truncate">{task.name}</div>
        <div className="cal-row-sub truncate">{listName}</div>
      </div>
      {showDue && task.dueDate !== null && <span className={clsx('cal-row-due', `tone-${tone}`)}>{formatDue(task.dueDate, !!task.dueTime)}</span>}
    </div>
  );
}

/** Body of the "+N more" popover. */
export function DayTasksPopover({ day, tasks, close }: { day: number; tasks: Task[]; close: () => void }) {
  return (
    <div className="cal-day-pop">
      <div className="cal-day-pop-head">
        <span className="cal-day-pop-dow">{format(day, 'EEEE')}</span>
        <span className="cal-day-pop-date">{format(day, 'MMM d')}</span>
      </div>
      <div className="cal-day-pop-list">
        {tasks.map((t) => (
          <DayPopRow key={t.id} task={t} close={close} />
        ))}
      </div>
    </div>
  );
}

function DayPopRow({ task, close }: { task: Task; close: () => void }) {
  const { status, progress, closed } = useTaskStatus(task);
  const openTask = useOpenTask();
  const time = task.dueTime && task.dueDate !== null ? formatClock(minutesOfDay(task.dueDate)) : null;
  return (
    <button
      className={clsx('cal-day-pop-row', { closed })}
      onClick={() => {
        close();
        openTask(task.id);
      }}
      onContextMenu={(e) => openContextMenu(e, (c) => <TaskContextMenu task={task} close={() => (c(), close())} />)}
    >
      <StatusIcon type={status.type} color={status.color} size={12} progress={progress} />
      {time && <span className="cal-chip-time">{time}</span>}
      <span className="truncate">{task.name}</span>
    </button>
  );
}

/** Inline "new task" input placed inside a day cell / hour slot. */
export function QuickAdd({ style, className, onSubmit, onCancel, label }: { style?: CSSProperties; className?: string; onSubmit: (name: string) => void; onCancel: () => void; label?: string }) {
  const [name, setName] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    requestAnimationFrame(() => ref.current?.focus());
  }, []);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div className={clsx('cal-quickadd', className)} style={style} onClick={stop} onPointerDown={stop} onMouseDown={stop} onContextMenu={stop}>
      {label && <span className="cal-quickadd-label">{label}</span>}
      <input
        ref={ref}
        value={name}
        placeholder="Task name"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter' && name.trim()) onSubmit(name.trim());
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => {
          if (!name.trim()) onCancel();
        }}
      />
      <span className="cal-quickadd-enter">↵</span>
    </div>
  );
}
